import React, { useEffect, useState } from "react";
import styles from './Evaluations.module.scss';
import LoadingOverlay from "../../../components/loadingOverlay/LoadingOverlay";
import PageTitle from "../../../components/pageTitle/PageTitle";
import { Tab, TabPanel, Tabs } from "../../../components/tabs/Tabs";
import Filter from "../../../components/filter/Filter";
import Button from "../../../components/button/Button";
import Modal from "../../../components/modal/Modal";
import EvaluationForm from "../../../components/evaluationForm/EvaluationForm";
import axiosClient from "../../../api/axiosClient";
import { useToasts } from "../../../contexts/ToastContext";
import EvaluationRequestStudentCard 
  from "../../../components/evaluationRequestStudentCard/EvaluationRequestStudentCard";
import LoadMore from "../../../components/loadMore/loadMore";
import LoadingSpinner from "../../../components/loadingSpinner/LoadingSpinner";
import { calculateTimeDifference, getDateOnly } from "../../../utils/timeUtils";
import EvaluationStudentCard from "../../../components/evaluationStudentCard/EvaluationStudentCard";
import { format } from "date-fns";
import CustomDateRangePicker from "../../../components/customDateRangePicker/CustomDateRangePicker";
import NewEvaluationForm from "../../../components/newEvaluationForm/NewEvaluationForm";
import BtTable from "../../../components/btTable/BtTable";
import { useNavigate } from "react-router-dom";

const Evaluations = () => {
  const limit = 20;
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState('');
  const [isEvaluationFormOpened, setIsEvaluationFormOpened] = useState(false);
  const [isNewEvaluationFormOpened, setIsNewEvaluationFormOpened] = useState(false);
  const [instructorList, setInstructorList] = useState([]);
  const [isFormLoading, setIsFormLoading] = useState(false);
  /* Requests Tab */
  const [requests, setRequests] = useState([]);
  const [requestsPage, setRequestsPage] = useState(1);
  const [requestsCount, setRequestsCount] = useState(0);
  const [requestsTotalPages, setRequestsTotalPages] = useState(1);
  const [isRequestsLoading, setIsRequestsLoading] = useState(false);
  const [refetchTrigger, setRefetchTrigger] = useState(false);
  const [startDate, setStartDate] = useState(new Date(new Date().setDate(new Date().getDate() - 6)));
  const [endDate, setEndDate] = useState(new Date());
  const [dateRange, setDateRange] = useState({ startDate, endDate });
  /* Pending Tab */
  const [isPendingLoading, setIsPendingLoading] = useState(false);
  const [pendingPage, setPendingPage] = useState(1);
  const [pendingFilter, setPendingFilter] = useState('Pending');
  const [pendingEvaluations, setPendingEvaluations] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [pendingTotalPages, setPendingTotalPages] = useState(1);
  /* Completed Tab */
  const [isCompletedLoading, setIsCompletedLoading] = useState(false);
  const [completedFilter, setCompletedFilter] = useState('Completed');
  const [completedPage, setCompletedPage] = useState(1);
  const [completedEvaluations, setCompletedEvaluations] = useState([]);
  const [completedCount, setCompletedCount] = useState(0);
  const [completedTotalPages, setCompletedTotalPages] = useState(1);
  const [isBenchmarksOpened, setIsBenchmarksOpened] = useState(false);
  const [benchmarksData, setBenchmarksData] = useState(null);
  const [newEvaluationFormData, setNewEvaluationFormData] = useState({});
  const [specialities, setSpecialities] = useState([]);
  const { showToast } = useToasts();
  const navigate = useNavigate();

  useEffect(() => {
    if(startDate && endDate) {
      setDateRange({ startDate, endDate });
    }
  }, [startDate, endDate]);
  
  useEffect(() => {
    const fetchData = async () => {
      if(requestsPage === 1) {
        setIsLoading(true);
      } else {
        setIsRequestsLoading(true);
      }
      const params = {
        limit: limit,
        page: requestsPage,
        startDate: format(dateRange.startDate, "yyyy-MM-dd"),
        endDate: format(dateRange.endDate, "yyyy-MM-dd"),
      };
      if(selectedFilter) {
        params.filter = selectedFilter;
      }
      await axiosClient.get(`evaluation-requests`, {
        params: params
      })
      .then ((response) => {
        if(response.data?.evaluationRequests) {
          if(requestsPage === 1) {
            setRequests([]);
          }
          setRequests((prevData) => {
            return [...prevData, ...response.data.evaluationRequests.map((request) => {
              return {
                id: request.id,
                date: request.date,
                data: [
                  {
                    label: 'Instructor',
                    text: `${request?.instructor?.firstName} ${request?.instructor?.lastName}` 
                  },
                  {
                    label: 'Clinical Site',
                    text: request?.clinicalSite?.name ?? ''
                  },
                  {
                    label: 'Request ID',
                    text: request.id
                  }
                ],
                status: request.status
              }
            })];
          });
          setRequestsCount(response.data.total)
          setRequestsTotalPages(response.data.totalPages);
        }
      })
      .catch((error) => {
        showToast(error.message, 'error');
      })
      .finally(() => {
        setIsLoading(false);
        setIsRequestsLoading(false);
      });
    }

    fetchData();
  }, [requestsPage, selectedFilter, showToast, dateRange, refetchTrigger]);
  
  useEffect(() => {
    // Reset to page 1 when selectedFilter changes
    setRequestsPage(1);
  }, [selectedFilter]);

  const requestsLoadMore = async () => {
    const page = requestsPage + 1;
    setRequestsPage(page);
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsPendingLoading(true);
      await axiosClient.get(`evaluations`, {
        params: {
          status: pendingFilter,
          limit: limit,
          page: pendingPage
        }
      })
      .then ((response) => {
        if(response.data?.evaluations) {
          if(pendingPage === 1) {
            setPendingEvaluations([]);
          }
          setPendingEvaluations((prevData) => {
            return [...prevData, ...response.data.evaluations.map((evaluation) => {
              const { isExpired, hours, timeDifference } = calculateTimeDifference(evaluation.missedDate);
              return {
                evaluationId: evaluation.id.toString(),
                timeRemaining: isExpired ? evaluation.status : timeDifference,
                timeColor: isExpired || hours < 2 ? 'red' : 'green',
                totalCount: evaluation.total,
                completedCount: evaluation.completed,
                isExpired: evaluation.status === 'Expired',
              };
            })];
          });
          setPendingCount(response.data.total)
          setPendingTotalPages(response.data.totalPages);
        }
      })
      .catch((error) => {
        showToast(error.message, 'error');
      })
      .finally(() => {
        setIsPendingLoading(false);
      });
    }

    fetchData();
  }, [pendingPage, pendingFilter, showToast]);

  useEffect(() => {
    setPendingPage(1);
    setPendingEvaluations([]);
  }, [pendingFilter]);

  const pendingLoadMore = async () => {
    const page = pendingPage + 1;
    setPendingPage(page);
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsCompletedLoading(true);
      await axiosClient.get(`evaluations`, {
        params: {
          status: completedFilter,
          limit: limit,
          page: completedPage
        }
      })
      .then ((response) => {
        if(response.data?.evaluations) {
          if(completedPage === 1) {
            setCompletedEvaluations([]);
          }
          setCompletedEvaluations((prevData) => {
            return [...prevData, ...response.data.evaluations.map((evaluation) => {
              return {
                evaluationId: evaluation.id.toString(),
                completedOn: getDateOnly(evaluation.completedDate),
                totalCount: evaluation.total,
                completedCount: evaluation.completed,
                isLate: evaluation.status === 'Late Submitted',
              }
            })];
          });
          setCompletedCount(response.data.total)
          setCompletedTotalPages(response.data.totalPages);
        }
      })
      .catch((error) => {
        showToast(error.message, 'error');
      })
      .finally(() => {
        setIsCompletedLoading(false);
      });
    }

    fetchData();
  }, [completedPage, completedFilter, showToast]);

  useEffect(() => {
    setCompletedPage(1);
    setCompletedEvaluations([]);
  }, [completedFilter]);

  const completedLoadMore = async () => {
    const page = completedPage + 1;
    setCompletedPage(page);
  };

  useEffect(() => {
    const fetchData = async () => {
      await axiosClient.get(`users/instructors`)
      .then((response) => {
        if(response.data?.instructors) {
          let instructors = [];
          response.data.instructors.forEach((item) => {
            item.clinicalSites.forEach(site => {
              instructors.push({
                instructorId: item.id,
                firstName: item.firstName,
                lastName: item.lastName,
                profilePicture: item.profilePicture,
                clinicalSiteId: site.id,
                clinicalSite: site.name,
                specialities: item.specialities ? item.specialities.map(speciality => speciality.name).join(',') : '',
                email: item.email ?? ''
              });
            })
          });
          setInstructorList(instructors);
        }
      })
    };

    fetchData();
  }, []);

  useEffect(() => {
    axiosClient.get(`specialities`)
    .then((response) => {
      if(response.data) {
        setSpecialities(response.data);
      }
    })
    .catch((error) => {
      showToast(error.message, 'error');
    });
  }, [showToast]);

  const handleCreateEvaluationRequest = async (formData) => {
    setIsFormLoading(true);
    await axiosClient.post(`evaluation-requests/create`, {
      ...formData,
    })
    .then((data) => {
      setIsEvaluationFormOpened(false);
      showToast(data.message);
      setRefetchTrigger(prev => !prev);
    })
    .catch((error) => {
      showToast(error.message, 'error');
    })
    .finally(() => {
      setIsFormLoading(false);
    });
  };

  const handleStartEvaluation = async () => {
    setIsBenchmarksOpened(true);
    if(benchmarksData === null) {
      await axiosClient.get(`benchmarks`)
      .then((response) => {
        setBenchmarksData(response.data);
      })
      .catch((error) => {
        showToast(error.message, 'error');
      });
    }
  }

  const handleAcknowledge = async () => {
    setIsLoading(true);
    await axiosClient.post(`evaluations`, newEvaluationFormData)
    .then((response) => {
      if(response.data) {
        navigate(`/evaluations/${response.data.id}`);
      } else {
        showToast('An error occurred. Please try again later ', 'error');
      }
    })
    .catch((error) => {
      showToast(error.message, 'error');
    })
    .finally(() => {
      setIsLoading(false);
    });
  };

  return (
    <>
      {isLoading && <LoadingOverlay />}
      <div className={styles.header}>
        <PageTitle
          label="Evaluations"
        />
        <div className={styles.cta}>
          <Button
            label="Create Evaluation"
            size="medium"
            variant="white"
            onClick={() => setIsNewEvaluationFormOpened(true)}
          />
          <Button
            label="Request Evaluation"
            size="medium"
            onClick={() => setIsEvaluationFormOpened(true)}
          />
        </div>
      </div>
      <Tabs>
        <Tab>
          <span>Requests</span>
          {requestsCount ? <span className={styles.count}>{requestsCount}</span> : ''}
        </Tab>
        <Tab>
          <span>On Going</span>
          {pendingCount ? <span className={styles.count}>{pendingCount}</span> : ''}
        </Tab>
        <Tab>
          <span>Completed</span>
          {completedCount ? <span className={styles.count}>{completedCount}</span> : ''}
        </Tab>

        <TabPanel>
          <div className={styles.container}>
            <div className={styles.topBar}>
              <div className={styles.filterWrapper}>
              <Filter
                filters={[
                  {
                    label: 'All',
                    value: '',
                  },
                  {
                    label: 'Accepted',
                    value: 'Accepted',
                  },
                  {
                    label: 'Declined',
                    value: 'Declined',
                  }
                ]}
                selected={selectedFilter}
                onChange={(filter) => {
                  setSelectedFilter(filter);
                }}
              />
              </div>
              <CustomDateRangePicker
                startDate={startDate}
                endDate={endDate}
                onChange={(start, end) => {
                  setStartDate(start);
                  setEndDate(end);
                }}
              />
            </div>
            <div className={styles.requests}>
              { requests.length > 0 ? <div className={styles.cardList}>
                {requests.map((request, index) => {
                  return (
                    <div className={styles.cardItem} key={`request-${index}`}>
                      <EvaluationRequestStudentCard
                        {...request}
                      />
                    </div>
                  );
                })}
                {(isRequestsLoading || requestsPage < requestsTotalPages) 
                  && <LoadMore isLoading={isRequestsLoading} onClick={requestsLoadMore} />}
              </div> : !isRequestsLoading ? <div className={styles.emptyEvaluations}>
                <p>No evaluation requests found.</p>
              </div> : <LoadingSpinner size={33} />}
            </div>
          </div>
        </TabPanel>

        <TabPanel>
          <div className={styles.container}>
            <Filter
              filters={[
                { label: 'All', value: 'Pending' },
                { label: 'Missed', value: 'Missed' },
              ]}
              selected={pendingFilter}
              onChange={setPendingFilter}
            />
            { pendingEvaluations.length > 0 ? <div className={styles.cardList}>
              {pendingEvaluations.map((item) => {
                return (
                  <div className={styles.cardItem} key={`pending-${item.evaluationId}`}>
                    <EvaluationStudentCard
                      data={[
                        { label: 'Evaluation ID', text: item.evaluationId },
                        { label: 'Time Remaining', text: item.timeRemaining, color: item.timeColor },
                      ]}
                      count={{
                        total: item.totalCount,
                        completed: item.completedCount
                      }}
                      disabled={item.isExpired}
                    />
                  </div>
                );
              })}
              {(isPendingLoading || pendingPage < pendingTotalPages) 
                && <LoadMore isLoading={isPendingLoading} onClick={pendingLoadMore} />}
            </div> : !isPendingLoading ? <div className={styles.emptyEvaluations}>
              <p>You have no pending evaluation.</p>
            </div> : <LoadingSpinner size={33} />}
          </div>
        </TabPanel>

        <TabPanel>
          <div className={styles.container}>
            <Filter
              filters={[
                { label: 'All', value: 'Completed' },
                { label: 'Late', value: 'Late' },
              ]}
              selected={completedFilter}
              onChange={setCompletedFilter}
            />
            { completedEvaluations.length > 0 ? <div className={styles.cardList}>
              {completedEvaluations.map((item) => {
                return (
                  <div className={styles.cardItem} key={`completed-${item.evaluationId}`}>
                    <EvaluationStudentCard
                      data={[
                        { label: 'Evaluation ID', text: item.evaluationId },
                        { label: 'Submitted Date', text: format(item.completedOn, 'MM/dd/yyyy'), tag: item.isLate ? 'Late' : undefined },
                      ]}
                      count={{
                        total: item.totalCount,
                        completed: item.completedCount
                      }}
                      cta={{
                        label: 'View Score',
                        link: `/evaluations/${item.evaluationId}/summary`
                      }}
                    />
                  </div>
                );
              })}
              {(isCompletedLoading || completedPage < completedTotalPages) 
                && <LoadMore isLoading={isCompletedLoading} onClick={completedLoadMore} />}
            </div> : !isCompletedLoading ? <div className={styles.emptyEvaluations}>
              <p>You have no completed evaluations at this time.</p>
            </div> : <LoadingSpinner size={33} />}
          </div>
        </TabPanel>
      </Tabs>
      {isEvaluationFormOpened && <Modal scrollable={false}>
        <EvaluationForm 
          instructorList={instructorList}
          onCancel={() => setIsEvaluationFormOpened(false)}
          onSubmit={handleCreateEvaluationRequest}
          isLoading={isFormLoading}
        />
      </Modal>}
      {isNewEvaluationFormOpened && <Modal scrollable={false}>
        <NewEvaluationForm
          instructorList={instructorList}
          specialities={specialities}
          onCancel={() => setIsNewEvaluationFormOpened(false)}
          onSubmit={(data) => {
            setNewEvaluationFormData(data);
            handleStartEvaluation()
          }}
          isLoading={isFormLoading}
        />
      </Modal>}
      {isBenchmarksOpened && <Modal>
        <BtTable
          data={benchmarksData}
          onCancel={() => setIsBenchmarksOpened(false)}
          onAcknowledge={() => {
            setIsBenchmarksOpened(false);
            setIsNewEvaluationFormOpened(false);
            handleAcknowledge();
          }}
        />
      </Modal>}
    </>
  );
};

export default Evaluations;